import * as React from "react";
import { Fab, Tooltip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import { colors, tokens } from "../../utils";

/**
 * Persistent floating help launcher (SPEC-v1 §7 "a persistent help launcher").
 * Toggles the panel open/closed. 56px FAB (> 44px touch target), navy brand
 * fill, visible keyboard focus ring.
 */
/**
 * This FAB's resting elevation. Named so the focus rule can put it back: the
 * app-wide focus treatment paints its white inner band with `box-shadow`, and a
 * shadow list cannot be appended to in CSS, so an elevated surface that does not
 * re-state its own shadow loses it for the duration of the focus. This is the
 * worked example the `--iy-focus-ring-keep` escape hatch in
 * `src/styles/focusRing.css` exists for.
 */
const FAB_ELEVATION = "0 6px 18px rgba(18,48,81,0.32)";

export default function HelpLauncher({ open, onToggle }) {
  return (
    <Tooltip title={open ? "Close help" : "Ask the Help Assistant"} placement="left">
      <Fab
        aria-label={open ? "Close help assistant" : "Open help assistant"}
        aria-expanded={open}
        onClick={onToggle}
        sx={{
          width: 56,
          height: 56,
          backgroundColor: colors.primary,
          color: colors.white,
          boxShadow: FAB_ELEVATION,
          "&:hover": { backgroundColor: colors.PrimaryPurple700 },
          // THE app-wide double ring, read from the token — never re-typed.
          // Measured on live staging 2026-08-02 (bundle main.cfc59c6c.js): this
          // FAB is filled #123051, where the white inner band measures 13.40:1,
          // and the purple outer band measures 3.76:1 against the #F6F7F9 page.
          // Both boundaries clear WCAG 2.2 SC 1.4.11's 3:1 floor.
          //
          // The width also moves 3px -> 2px here: it was the only 3px ring in the
          // measured header cluster, and one geometry app-wide is the point.
          "&:focus-visible": {
            outline: `${tokens.focusRing.width}px solid ${tokens.focusRing.color}`,
            outlineOffset: tokens.focusRing.innerWidth,
            boxShadow: `${tokens.focusRing.innerShadow}, ${FAB_ELEVATION}`,
          },
        }}
      >
        {open ? (
          <CloseIcon sx={{ fontSize: 26 }} />
        ) : (
          <HelpOutlineIcon sx={{ fontSize: 28 }} />
        )}
      </Fab>
    </Tooltip>
  );
}
